import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { getBusiness, updateBusiness } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DiagnosisSection } from "@/components/nadia/DiagnosisSection";
import { toast } from "sonner";

const LISTS = ["locations", "services", "products", "targetAudience"];

const OFFER_STATUS = {
  passata: ["Scaduta", "border-rose-500/30 text-rose-300"], attiva: ["Attiva", "border-emerald-500/30 text-emerald-300"],
  in_scadenza: ["In scadenza", "border-amber-500/30 text-amber-300"], futura: ["Futura", "border-cyan-500/30 text-cyan-300"],
  scade_oggi: ["Scade oggi", "border-amber-500/30 text-amber-300"], senza_data: ["Validità da confermare", "border-white/20 text-slate-400"],
  proposta: ["Proposta", "border-white/20 text-slate-400"], non_attiva: ["Non attiva", "border-white/20 text-slate-400"],
};

function offerDate(value) {
  if (typeof value !== "string") return null;
  const iso = value.trim().match(/^(\d{4}-\d{2}-\d{2})(?:T.*)?$/);
  const local = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  const date = iso?.[1] || (local ? `${local[3]}-${local[2].padStart(2, "0")}-${local[1].padStart(2, "0")}` : null);
  const parsed = date ? new Date(`${date}T00:00:00Z`) : null;
  return parsed && Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : null;
}

function currentOffers(items) {
  const parts = new Intl.DateTimeFormat("en", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const today = ["year", "month", "day"].map((type) => parts.find((p) => p.type === type).value).join("-");
  return (items || []).map((item, index) => {
    const offer = typeof item === "string" ? { title: item } : item;
    const rawStart = offer.valid_from || offer.start_date;
    const start = offerDate(rawStart), end = offerDate(offer.valid_to || offer.end_date);
    let status = "senza_data";
    if (end && end < today) status = "passata";
    else if (offer.active === false) status = "non_attiva";
    else if (offer.source === "proposal" || ["proposta", "proposed"].includes(offer.status)) status = "proposta";
    else if (end && (!rawStart || start) && (!start || start <= end)) {
      if (start && start > today) status = "futura";
      else if (end === today) status = "scade_oggi";
      else status = (Date.parse(end) - Date.parse(today)) / 86400000 <= 7 ? "in_scadenza" : "attiva";
    }
    return { ...offer, title: offer.title || offer.name, valid_from: start, valid_to: end, status, index };
  });
}

const normalized = (text) => String(text || "").toLocaleLowerCase("it-IT").replace(/\s+/g, " ").trim();
const words = (text) => (normalized(text).match(/\p{L}{4,}/gu) || []).map((word) => word.length > 4 ? word.replace(/[aeio]$/, "") : word);
const REVIEW_REFERENCE = /recension|testimonianz|testimonials?|reviews?\b|ratings?\b|\bstelle\b|valutazion[ei]\s+(?:media|positiv[ae]|(?:dei\s+)?clienti)|punteggio\s+medio|tripadvisor|trustpilot|social proof|riprova sociale|feedback dei clienti/i;

export default function BusinessBrain() {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const startForm = useCallback((biz) => setForm(Object.fromEntries(["business", "industry", "brandTone", ...LISTS].map((k) => [k, Array.isArray(biz?.[k]) ? biz[k].map((x) => (typeof x === "string" ? x : x?.name || x?.title || "")).join(", ") : biz?.[k] || ""]))), []);
  const saveEdit = async (e) => {
    e.preventDefault();
    const patch = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, LISTS.includes(k) ? String(v).split(",").map((x) => x.trim()).filter(Boolean) : v]));
    try { const r = await updateBusiness(patch); setB(r); setEditing(false); toast.success("Business Brain aggiornato: grazie, ora lavoro su dati confermati"); } catch (err) { toast.error(err?.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  const [b, setB] = useState(null);
  useEffect(() => {
    getBusiness().then((biz) => { setB(biz); startForm(biz); }).catch(() => setB(null));
  }, [startForm]);

  if (!b)
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-60 bg-white/5" />
        <Skeleton className="h-96 bg-white/5" />
      </div>
    );

  // Read-only projection for this card: saved facts and other Business Brain consumers stay unchanged.
  const offers = currentOffers(b.offers);
  const activeOffers = offers.filter((o) => ["attiva", "in_scadenza"].includes(o.status));
  const otherOffers = offers.filter((o) => !["attiva", "in_scadenza"].includes(o.status));
  const sourceUrls = new Set((b.sources || []).map((source) => source.url).filter(Boolean));
  const verifiedQuotes = (b.facts || []).filter((fact) => fact.source_type === "public_source" && fact.verification && sourceUrls.has(fact.source_url))
    .map((fact) => normalized(fact.value)).filter(Boolean);
  const supportedReviews = (text) => !REVIEW_REFERENCE.test(text) || verifiedQuotes.some((quote) => quote.includes(normalized(text)));
  const genericWords = new Set(words(`offerta offerte promozione promozioni promo pacchetto pacchetti sconto sconti speciale speciali validità valida valido fino entro oggi vista festeggia scopri prenota richiedi iscriviti primo nuova nuove ${b.business || ""} ${(b.locations || []).join(" ")}`));
  const excludedReferences = otherOffers.map((offer) => {
    const description = normalized(`${offer.title || ""} ${offer.description || offer.note || ""}`);
    return {
      title: normalized(offer.title),
      topic: /newsletter/i.test(description) ? "newsletter" : words(offer.title).find((word) => !genericWords.has(word)),
      discounts: description.match(/\d+(?:[.,]\d+)?\s*%/g) || [],
    };
  });
  const safeInsight = (text) => {
    const value = normalized(text), tokens = new Set(words(text));
    return supportedReviews(text) && !excludedReferences.some((offer) =>
      (offer.title && value.includes(offer.title)) || (offer.topic && tokens.has(offer.topic)) ||
      (/scont|offert|promoz|newsletter|iscriv/i.test(value) && offer.discounts.some((discount) => value.replace(/\s*%/g, "%").includes(discount.replace(/\s*%/g, "%")))));
  };
  const filtered = (items, check) => (items || []).filter((text) => typeof text === "string" && text.trim() && check(text));
  const visibleBrain = {
    ...b,
    evidence: b.evidence ? Object.fromEntries(Object.entries(b.evidence).map(([key, items]) => [key, filtered(items, supportedReviews)])) : b.evidence,
    diagnosis: b.diagnosis ? {
      ...b.diagnosis,
      opportunities: filtered(b.diagnosis.opportunities, safeInsight),
      problems: filtered(b.diagnosis.problems, supportedReviews),
      hypotheses_to_test: filtered(b.diagnosis.hypotheses_to_test, safeInsight),
      priority_actions: (b.diagnosis.priority_actions || []).filter((action) => safeInsight(Object.values(action).filter((value) => typeof value === "string").join(" "))),
    } : b.diagnosis,
    questions_for_owner: filtered(b.questions_for_owner, safeInsight),
  };
  const insights = filtered(b.marketingInsights, safeInsight);

  return (
    <>
      <PageHeader
        eyebrow="Business Brain"
        title="Ciò che Nadia sa di te"
        subtitle="Più contesto ha Nadia, migliori sono le decisioni che prende per la tua attività."
      />
      <div className="nadia-card p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="brain-confirm">
        <div className="text-sm text-slate-200">Ho capito bene la tua attività? Puoi correggere quello che ho rilevato.</div>
        <Button size="sm" variant="outline" className="border-white/10" onClick={() => setEditing(!editing)} data-testid="brain-edit-toggle">{editing ? "Chiudi" : "Modifica informazioni"}</Button>
      </div>
      {editing && (
        <form onSubmit={saveEdit} className="nadia-card p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brain-edit-form">
          {[["business", "Nome attività"], ["industry", "Settore"], ["locations", "Località (separate da virgola)"], ["services", "Servizi (separati da virgola)"], ["products", "Prodotti (separati da virgola)"], ["targetAudience", "Pubblico (separato da virgola)"], ["brandTone", "Tono di voce"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400 space-y-1"><span>{l}</span>
              <input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="block w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 px-3 text-sm text-slate-100" data-testid={`brain-edit-${k}`} /></label>
          ))}
          <div className="md:col-span-2"><Button type="submit" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold" data-testid="brain-edit-save">Salva e conferma</Button></div>
        </form>
      )}

      {/* Hero card */}
      <div className="nadia-card p-6 mb-6" data-testid="business-hero-card">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {b.images?.[0] && (
            <img
              src={b.images[0]}
              alt=""
              className="w-full sm:w-40 h-40 object-cover rounded-2xl border border-white/10"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-cyan-300 uppercase tracking-wider font-semibold mb-1">
              {b.industry}
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-50 mb-1">{b.business}</h2>
            <a href={b.website} target="_blank" rel="noreferrer" className="text-sm text-slate-400 hover:text-cyan-300">
              {b.website}
            </a>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              {b.brandTone} · {b.locations?.join(", ")}
            </p>
          </div>
        </div>
      </div>

      <DiagnosisSection biz={visibleBrain} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Section title="Prodotti e servizi">
          <ul className="space-y-2">
            {[...(b.services || []), ...(b.products || [])].length === 0 && <li className="text-sm text-slate-500 italic">Da completare</li>}
            {[...(b.services || []), ...(b.products || [])].map((p, i) => {
              const name = typeof p === "string" ? p : p?.name || p?.title;
              const price = typeof p === "object" && p?.price ? `€${p.price}` : null;
              if (!name) return null;
              return (
                <li key={`${name}-${i}`} className="flex justify-between text-sm gap-3">
                  <span className="text-slate-200">{name}</span>
                  {price && <span className="font-mono text-slate-400">{price}</span>}
                </li>
              );
            })}
          </ul>
        </Section>

        <Section title="Target">
          <ul className="space-y-1 text-sm text-slate-200">
            {b.targetAudience?.map((t, i) => (
              <li key={i}>· {t}</li>
            ))}
          </ul>
        </Section>

        <Section title="Offerte">
          {[
            { key: "active", title: "Offerte attive", items: activeOffers, empty: "Nessuna offerta con validità corrente documentata." },
            { key: "excluded", title: "Offerte scadute, future o da confermare", items: otherOffers, empty: "Nessuna offerta esclusa." },
          ].map((group) => (
            <div key={group.key} className="mb-4 last:mb-0" data-testid={`brain-offers-${group.key}`}>
              <h3 className="text-xs font-semibold text-slate-300 mb-2" data-testid={`brain-offers-${group.key}-title`}>{group.title}</h3>
              {group.items.length ? group.items.map((o) => (
                <div key={o.id || o.index} className="mb-3" data-testid={`offer-${o.index}`}>
                  <div className="text-sm text-slate-100 font-semibold flex items-center gap-2 flex-wrap">
                    <span data-testid={`offer-${o.index}-title`}>{supportedReviews(o.title) ? o.title : "Offerta non documentata nelle fonti disponibili"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${OFFER_STATUS[o.status][1]}`} data-testid={`offer-${o.index}-status`}>{OFFER_STATUS[o.status][0]}</span>
                  </div>
                  <div className="text-xs text-slate-400" data-testid={`offer-${o.index}-details`}>
                    {supportedReviews(o.description || o.note) ? o.description || o.note : null}
                    {(o.valid_from || o.valid_to) && <div data-testid={`offer-${o.index}-dates`}>{[
                      o.valid_from && `dal ${o.valid_from.split("-").reverse().join("/")}`,
                      o.valid_to && `al ${o.valid_to.split("-").reverse().join("/")}`,
                    ].filter(Boolean).join(" · ")}</div>}
                    {o.status === "senza_data" && <div data-testid={`offer-${o.index}-validity-missing`}>Validità non documentata: non considerata attiva.</div>}
                  </div>
                </div>
              )) : <p className="text-sm text-slate-400" data-testid={`brain-offers-${group.key}-empty`}>{group.empty}</p>}
            </div>
          ))}
        </Section>

        <Section title="Capacità e orari">
          <div className="text-sm text-slate-200">
            {b.capacity?.seats ? `${b.capacity.seats} posti${b.capacity.turns_per_evening ? ` · ${b.capacity.turns_per_evening} turni` : ""}` : <span className="text-slate-500 italic">Da completare</span>}
          </div>
          <div className="mt-2 text-sm text-slate-400 space-y-0.5">
            {Object.entries(b.openingHours || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="capitalize">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Insight marketing" className="md:col-span-2">
          <ul className="space-y-2">
            {insights.map((i, idx) => (
              <li key={idx} className="text-sm text-slate-200" data-testid={`brain-marketing-insight-${idx}`}>
                <span className="text-cyan-300 mr-2">◆</span>
                {i}
              </li>
            ))}
            {!insights.length && <li className="text-sm text-slate-400" data-testid="brain-marketing-insights-empty">Nessun insight utilizzabile con le offerte e le fonti documentate disponibili.</li>}
          </ul>
        </Section>
      </div>
    </>
  );
}

function Section({ title, children, className = "" }) {
  return (
    <div className={`nadia-card p-5 ${className}`} data-testid={`brain-section-${title}`}>
      <div className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}
